import { Helmet } from 'react-helmet-async';
import { Card } from 'components';
import useBankIdConfirm from 'pages/Registration/BankId/BankIdConfirm/useBankIdConfirm';
import H1 from 'components/H/H1';
import { TwButton } from 'components/TwButton/TwButton';
import { useTranslation } from 'react-i18next';
import { formatBirthNumber } from 'utils/birthNumber';

type ItemProps = {
  title: string;
  value?: string;
  isLarge?: boolean;
  dataTest?: string;
  skeletonClassName?: string;
};
// TODO check if still relevant
function Item({
  title,
  value,
  isLarge = false,
  dataTest,
  skeletonClassName,
}: ItemProps) {
  const skeletonCn = `animate-pulse bg-background-accent ${
    skeletonClassName ?? ''
  }`;
  return (
    <div
      className="flex flex-col gap-2 border-b py-3 last-of-type:border-b-0 last-of-type:mb-8"
      data-test={dataTest}
    >
      <div className="text-xs text-muted h-">{title}</div>
      <div
        className={`${value ? '' : skeletonCn} ${
          isLarge ? 'text-2xl font-bold h-8' : 'h-6'
        } `}
      >
        {value}
      </div>
    </div>
  );
}

function BankIdConfirm() {
  const { t } = useTranslation();
  const {
    isPending,
    isLoading,
    info,
    handleRejectedBankID,
    handleConfirmBankID,
  } = useBankIdConfirm();

  const titleCardNumberType = info?.typeOfCard || 'ID_CARD';
  const birthNumberFormat = formatBirthNumber(info?.birthnumber);

  const items = [
    {
      dataTest: 'name',
      title: t('bank_id_confirm:form.full_name'),
      value: info?.fullName,
      isLarge: true,
      skeletonClassName: 'w-1/3',
    },

    {
      dataTest: 'full-age',
      title: t('bank_id_confirm:form.full_age'),
      value: info?.fullAge,
      skeletonClassName: 'w-1/4',
    },
    {
      dataTest: 'personal-id',
      title: t('bank_id_confirm:form.personal_Id'),
      value: birthNumberFormat,
      skeletonClassName: 'w-1/2',
    },
    {
      dataTest: 'id-card-number',
      title: t(`bank_id_confirm:form.${titleCardNumberType}`),
      value: info?.cardNumber,
      skeletonClassName: 'w-1/4',
    },
    {
      dataTest: 'address',
      title: t('bank_id_confirm:form.address'),
      value: info?.fullAddress,
      skeletonClassName: 'w-3/4',
    },
  ];

  return (
    <div data-test="bank-id-confirm">
      <Helmet>
        <title>{t('bank_id_confirm:html_title')}</title>
      </Helmet>
      <div className="px-5 lg:px-0 text-center lg:text-left bg-white lg:bg-inherit py-6 lg:pt-0">
        <H1>{t('bank_id_confirm:title')}</H1>
        <p>{t('bank_id_confirm:info')}</p>
      </div>

      <Card className="flex flex-col rounded-none lg:rounded px-5">
        {items.map(({ title, dataTest, value, isLarge, skeletonClassName }) => (
          <Item
            key={title}
            title={title}
            dataTest={dataTest}
            value={value}
            isLarge={isLarge}
            skeletonClassName={skeletonClassName}
          />
        ))}
        <TwButton
          onClick={handleConfirmBankID}
          pending={isPending}
          data-test="continue"
          disabled={isLoading || isPending}
        >
          {t('bank_id_confirm:btn')}
        </TwButton>
      </Card>

      <button
        type="button"
        className="text-primary mt-4 text-left mb-10 hover:underline ml-5 lg:ml-0 text-sm lg:text-base"
        onClick={handleRejectedBankID}
        data-test="skip"
        disabled={isPending}
      >
        {t('bank_id_confirm:link_text')}
      </button>
    </div>
  );
}

export default BankIdConfirm;
